import React, { useEffect, useState } from 'react';
import { ArrowUp, Printer } from 'lucide-react';
import Card from '../../components/common/Card';
import MainLayout from '../../components/layout/MainLayout';

/**
 * LegalLayout - 법적 페이지(약관/개인정보취급방침 등) 공통 레이아웃.
 * - 목차(앵커 네비게이션) + "맨 위로" 버튼 + 인쇄 버튼을 표준화한다(DRY).
 *
 * @param {Object} props
 * @param {string} props.title - 페이지 제목
 * @param {string} [props.lastUpdated] - 최종 수정일 텍스트
 * @param {Array<{id:string,label:string}>} [props.sections] - 목차 항목
 * @param {React.ReactNode} props.children - 본문(각 조항 section, id 부여 권장)
 */
const LegalLayout = ({ title, lastUpdated, sections = [], children }) => {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="min-h-[calc(100vh-200px)] bg-secondary-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card>
            <div className="p-8">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <h1 className="text-3xl font-bold text-primary">{title}</h1>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-accent-300 px-3 py-1.5 text-sm text-accent-700 hover:bg-accent-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 print:hidden"
                >
                  <Printer size={16} aria-hidden="true" />
                  인쇄 / PDF 저장
                </button>
              </div>

              {lastUpdated && (
                <p className="text-sm text-accent-600 mb-6">
                  최종 수정일: {lastUpdated}
                </p>
              )}

              {/* 목차 (앵커 네비게이션) */}
              {sections.length > 0 && (
                <nav
                  aria-label="목차"
                  className="mb-8 rounded-lg border border-accent-200 bg-secondary-50 p-4 print:hidden"
                >
                  <h2 className="mb-2 text-sm font-semibold text-primary">목차</h2>
                  <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="text-sm text-primary-700 hover:underline"
                        >
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {/* 본문 */}
              <div className="space-y-8 text-accent-700 scroll-smooth">
                {children}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* 맨 위로 버튼 */}
      {showTop && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="맨 위로 이동"
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-900 text-white shadow-lg transition-opacity hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 print:hidden"
        >
          <ArrowUp size={20} aria-hidden="true" />
        </button>
      )}
    </MainLayout>
  );
};

export default LegalLayout;
